/**
 * Field Help — Deals
 *
 * Keys MUST match the deal field ids in
 * `features/deals/config/fields/*.ts` (see DEAL_FIELDS / DealFieldId) so the "?"
 * popover attaches to the right column / editor row / card field.
 *
 * Relationship context (used in `relatedTo`): a deal belongs to exactly ONE
 * company; deal → contact ALWAYS resolves through that company. Tasks and
 * tickets attach to the deal for project delivery. See
 * `lib/actions/relationships/AGENTS.md`.
 */

import type { EntityFieldHelp } from "./types";

export const dealFieldHelp: EntityFieldHelp = {
  title: {
    title: "Deal Title",
    description: "Short name for the opportunity — what you're selling and to whom.",
    usage: "Keep it scannable in the pipeline (e.g. \"Acme — Managed IT + M365\"). It's the headline on every deal card and reminder.",
  },
  revenue: {
    title: "Revenue",
    description: "Total contract value of the deal in dollars (one-time or full-term).",
    usage: "Drives weighted pipeline and the CEO forecast. Net cost is subtracted from revenue to compute deal profitability — keep it accurate so margin and forecast numbers are trustworthy.",
    relatedTo: "Combined with Cost to derive Margin %. Feeds the company's total deal value.",
  },
  currency: {
    title: "Currency",
    description: "Currency the revenue and cost figures are denominated in.",
    usage: "Leave as USD for standard WTC engagements; only change for a client billed in another currency.",
  },
  stage: {
    title: "Stage",
    description:
      "Where the deal sits in the lifecycle — sales (lead through contract signed), deal won and handoff, delivery (started through paid), or a specific lost reason (SM/WTC variants).",
    usage: "Stage drives the weighted forecast and follow-up reminders. Move it promptly — pipeline reminders cover active sales and delivery stages; deal won and paid deals roll into committed revenue.",
  },
  close_probability: {
    title: "Close Probability",
    description: "Your estimated likelihood (0–100%) that the deal closes.",
    usage: "Revenue × probability = the deal's weighted value in the forecast. Set it honestly; inflated probabilities distort the CEO pipeline view.",
    relatedTo: "Combined with Revenue to produce weighted pipeline on the CEO dashboard.",
  },
  close_date: {
    title: "Expected Close Date",
    description: "When you expect the deal to close.",
    usage: "Buckets the deal into a forecast period. Keep it realistic — it's how revenue is attributed to a month/quarter for targets.",
  },
  company_id: {
    title: "Company",
    description: "The company this deal belongs to. Every deal has exactly one company.",
    usage: "Set this first — it's how the deal connects to people. Contacts are reached through the company, never linked to the deal directly.",
    relatedTo: "Deal → company → contact. Pick which contact at the company when emailing, calling, or texting from the deal.",
  },
  company_name: {
    title: "Company Name",
    description: "Display name of the deal's company (denormalized from the linked company).",
    usage: "Read-only label for scanning the pipeline. Change the company link to change this.",
    relatedTo: "Mirrors the linked Company record.",
  },
  is_recurring: {
    title: "Recurring Deal",
    description: "Marks the deal as a subscription / retainer rather than one-time revenue.",
    usage: "Turn on for MSP retainers and recurring services so Monthly Amount and committed MRR are tracked separately from one-time revenue.",
    relatedTo: "Unlocks Monthly Amount; recurring deals feed committed MRR on the CEO dashboard.",
  },
  monthly_amount: {
    title: "Monthly Amount",
    description: "Recurring monthly revenue (MRR) when the deal is recurring.",
    usage: "Set for retainers/subscriptions. Rolls into committed MRR forecasting — the backbone of predictable MSP revenue.",
    relatedTo: "Only meaningful when Recurring Deal is on.",
  },
  is_committed: {
    title: "Committed",
    description: "Flags revenue as committed (signed / highly certain) vs. speculative pipeline.",
    usage: "Use to separate locked-in revenue from open pipeline in forecasting. Committed deals carry more weight in the CEO revenue view.",
  },
  cost: {
    title: "Net Cost",
    description: "Estimated cost to deliver the deal (labor, hard costs, licenses).",
    usage: "Revenue minus Cost is the profit. Fill it in so Margin % is real — this is how the company judges whether an engagement is worth taking.",
    relatedTo: "Drives Margin % alongside Revenue.",
  },
  margin_percentage: {
    title: "Margin %",
    description: "Profitability of the deal as a percentage: (Revenue − Cost) / Revenue.",
    usage: "Watch low-margin deals — they consume delivery capacity for little profit. Useful when prioritizing which deals to chase.",
    relatedTo: "Computed from Revenue and Net Cost.",
  },
  estimated_hours: {
    title: "Estimated Hours",
    description: "Expected engineering/delivery hours to fulfill the deal.",
    usage: "Feeds capacity and staffing planning — large hour estimates against thin staffing surface as a risk on the CEO health view.",
    relatedTo: "Compared against logged time entries during delivery.",
  },
  tags: {
    title: "Tags",
    description: "Free-form labels for segmenting deals (e.g. priority, vertical, campaign).",
    usage: "Also where the system stores weekly follow-up and snooze markers. Use plain tags for your own segmentation; the follow-up/snooze tags are managed by their actions.",
  },
  description: {
    title: "Description",
    description: "What the engagement covers — scope, deliverables, context.",
    usage: "The shared understanding of the deal. Keep enough detail that an engineer or owner can grasp the work without asking.",
  },
  notes: {
    title: "Notes",
    description: "Internal running notes on the deal.",
    usage: "Negotiation context, discounts approved, internal caveats. Mark-won/lost actions append outcome notes here automatically.",
  },
  assigned_users: {
    title: "Owners",
    description: "Salespeople responsible for the deal; the first is the primary owner.",
    usage: "The primary owner gets the follow-up reminders. Use \"Assign to me\" to take ownership. Unowned active deals surface as a risk.",
    relatedTo: "Drives who receives Slack follow-up DMs for this deal.",
  },
  engineer_ids: {
    title: "Engineers",
    description: "Engineers assigned to deliver the work once the deal is sold.",
    usage: "Set during delivery so staffing and utilization reflect who's doing the work.",
    relatedTo: "Connects the deal to delivery capacity and time tracking.",
  },
  schedule_status: {
    title: "Schedule Health",
    description: "Delivery health signal for whether the deal is on schedule.",
    usage: "One of the four project-health signals. Flip to at-risk early so delivery problems surface on the CEO health view before they hurt the client.",
    relatedTo: "Part of the project-health set with Budget, Scope, and Alignment.",
  },
  budget_status: {
    title: "Budget Health",
    description: "Delivery health signal for whether the deal is on budget.",
    usage: "Flag overruns early — budget slippage erodes the Margin you sold the deal on.",
    relatedTo: "Part of the project-health set; relates to Net Cost / Margin %.",
  },
  scope_status: {
    title: "Scope Health",
    description: "Delivery health signal for scope creep / scope stability.",
    usage: "Use to flag when a client is expanding scope beyond what was sold — a cue to re-quote or set boundaries.",
    relatedTo: "Part of the project-health set.",
  },
  alignment_status: {
    title: "Alignment Health",
    description: "Delivery health signal for client alignment / relationship health.",
    usage: "Surfaces relationship risk independent of schedule and budget — a green-budget deal with poor alignment can still churn.",
    relatedTo: "Part of the project-health set.",
  },
  created_at: {
    title: "Created",
    description: "When the deal record was created.",
    usage: "Read-only. Useful for pipeline aging — how long an opportunity has been open.",
  },
  updated_at: {
    title: "Last Updated",
    description: "When the deal was last modified.",
    usage: "Read-only. Follow-up reminders order by this so the most-neglected deals surface first.",
  },
  last_communication_at: {
    title: "Last Communication",
    description: "Timestamp of the most recent logged communication on the deal.",
    usage: "A staleness signal — a long gap on an active deal is a prompt to follow up.",
    relatedTo: "Updated when communications are logged against the deal/contact.",
  },
  won_at: {
    title: "Won At",
    description: "When the deal was marked won.",
    usage: "Read-only — set by the Mark as Won action. Anchors won-revenue reporting.",
  },
  lost_at: {
    title: "Lost At",
    description: "When the deal was marked lost.",
    usage: "Read-only — set by the Mark as Lost action. Used for loss analysis.",
  },
};
